import { InlineOption } from "@/Common/Components/Inputs/InlineOptionsControl";
import { Patient } from "@/Common/Models/Patient/Patient";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { BaseOntarioObstetricalFileSubSection, BaseOntarioObstetricalFileSubSectionProps } from "@/Patient/Components/MedicalNote/Obstetric/Form/Ontario/Subcomponent/BaseOntarioObstetricalFileSubSection";
import { ObstetricalCounter, ObstetricalCountersComponent } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/ObstetricalCountersComponent";
import { ObstetricsCalculator } from "@/Patient/Helpers/ObstetricsCalculator";
import { OntarioObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/Ontario/OntarioObstetricalFileForm";
import { OntarioObstetricalPregnancySummary } from "@/Patient/Models/ObstetricalFile/Ontario/OPR1/OntarioObstetricalPregnancySummary";
import { PregnancyDatingMethod } from "@/Patient/Models/ObstetricalFile/Ontario/OPR1/PregnancyDatingMethodEnum";

interface IOntarioObstetricalFilePregnancySummaryProps extends BaseOntarioObstetricalFileSubSectionProps {
    renderFinalEstimatedBirthDate: JSX.Element;
    handleObstetricalCountersUpdate: () => void;
    patient: Patient;
    contributionId: string;
}

class OntarioObstetricalFilePregnancySummaryState {
    public displayIgnoreDaysInCycle: boolean;
}

class OntarioObstetricalFilePregnancySummary extends BaseOntarioObstetricalFileSubSection<IOntarioObstetricalFilePregnancySummaryProps, OntarioObstetricalFilePregnancySummaryState> {
    public componentWillMount(): void {
        super.componentWillMount();
        this.mutateState(s => s.displayIgnoreDaysInCycle = !!this.props.form.Opr1.PregnancySummary.LastMenstrualPeriodDate);
    }

    public render(): JSX.Element {
        return (
            <div className="section border border-top pregnancysummary">
                {this.renderSubTitle("ObstetricalFile_PregnancySummary")}
                {this.renderPregnancySummarySection()}
                {this.renderPregnancySummaryObstetricalCounters()}
            </div>
        );
    }

    private renderPregnancySummarySection(): JSX.Element {
        return (
            <div className="row">
                <div className="col-md-8">
                    <div className="row row-form">
                        <div className="col-md-8 flex-display flex-wrap flex-space-between">
                            {this.renderSimpleField(
                                "ObstetricalFile_LMP",
                                this.controlRenderer.renderDateSelector({
                                    value: this.props.form.Opr1.PregnancySummary.LastMenstrualPeriodDate,
                                    onChange: value => this.mutateState(
                                        s => s.displayIgnoreDaysInCycle = !!value,
                                        () => this.onChangeLastMenstrualPeriodChange(value)
                                    ),
                                    id: "pregnancySummaryLMP",
                                    className: "ontario-section-pregnancysummary-widthdate"
                                }),
                                "margin-md-right"
                            )}
                            {this.renderSimpleField(
                                "ObstetricalFile_Cycleq",
                                this.controlRenderer.renderTextbox({
                                    value: this.props.form.Opr1.PregnancySummary.MenstrualCycleLength?.toString(),
                                    onChange: value => this.onChangeDaysInCycleChange(value),
                                    maxLength: 2,
                                    isNumeric: true,
                                    className: "table-textbox",
                                    debounceMs: 400
                                }),
                                "margin-md-right cycle-q"
                            )}
                            {this.renderRadioButtonField(
                                "ObstetricalFile_Certain",
                                this.controlRenderer.renderInlineYesNoRadio(
                                    this.props.form.Opr1.PregnancySummary.IsLastMenstrualPeriodDateCertain,
                                    value => this.props.mutateModelState(s => s.Opr1.PregnancySummary.IsLastMenstrualPeriodDateCertain = value),
                                    "Certain radio-btn-min-height"
                                ),
                                "margin-md-right"
                            )}
                            {this.renderRadioButtonField(
                                "ObstetricalFile_Regular",
                                this.controlRenderer.renderInlineYesNoRadio(
                                    this.props.form.Opr1.PregnancySummary.IsMenstrualCycleRegular,
                                    value => this.props.mutateModelState(s => s.Opr1.PregnancySummary.IsMenstrualCycleRegular = value),
                                    "Regular radio-btn-min-height"
                                )
                            )}
                        </div>
                        <div className="col-md-4">
                            <div className="flex-column">
                                <div className="field-and-label-container">
                                    <div className="field-label">{this.getString("ObstetricalFile_EdbByLmp")}</div>
                                    <div className="field-value">
                                        {this.controlRenderer.renderDateSelector({
                                            value: this.props.form.Opr1.PregnancySummary.EstimatedDateOfBirthByLastMenstrualPeriodDate,
                                            onChange: value => this.props.mutateModelStateWithFormUpdate(s => {
                                                s.Opr1.PregnancySummary.EstimatedDateOfBirthByLastMenstrualPeriodDate = value;
                                                this.updateFinalEstimatedDateOfBirthByLastMenstrualPeriod(s);
                                            }),
                                            id: "EDCByLMPDateSelector"
                                        })}
                                    </div>
                                </div>
                                {!this.props.readonly
                                    && this.state.displayIgnoreDaysInCycle
                                    && this.controlRenderer.renderCheckbox(
                                        "IgnoreNumberOfDaysInCycleLabel",
                                        this.props.form.Opr1.PregnancySummary.IgnoreNumberOfDaysToEstimateDueDate,
                                        () => this.onChangeIgnoreDaysInCycleChange(),
                                        "ignoreDaysInCycle-checkbox margin-xs-top"
                                    )
                                }
                            </div>
                        </div>
                    </div>
                    <div className="row row-form">
                        {this.renderRadioButtonField(
                            "ObstetricalFile_PlannedPregnancy",
                            this.controlRenderer.renderInlineYesNoRadio(
                                this.props.form.Opr1.PregnancySummary.IsPregnancyPlanned,
                                value => this.props.mutateModelState(s => s.Opr1.PregnancySummary.IsPregnancyPlanned = value),
                                "PlannedPregnancy radio-btn-min-height"
                            ),
                            "col-md-3"
                        )}
                        <div className="col-md-5 flex-display flex-wrap flex-space-between">
                            {this.renderSimpleField(
                                "ObstetricalFile_ContraceptiveType",
                                this.controlRenderer.renderAutoCompleteTextbox({
                                    value: this.props.form.Opr1.PregnancySummary.ContraceptiveType,
                                    autoCompleteItems: this.contraceptiveTypeOptions(),
                                    onChange: value => this.props.mutateModelState(s => s.Opr1.PregnancySummary.ContraceptiveType = value),
                                    onAutoCompleteSelected: (code, _, value) => this.props.mutateModelState(s => s.Opr1.PregnancySummary.ContraceptiveType = value),
                                    maxLength: 10,
                                    disableMatchAutoSorting: true
                                })
                            )}
                            {this.renderSimpleField(
                                "ObstetricalFile_LastUsed",
                                this.controlRenderer.renderDateSelector({
                                    value: this.props.form.Opr1.PregnancySummary.LastUsedContraceptiveDate,
                                    onChange: value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.PregnancySummary.LastUsedContraceptiveDate = value),
                                    id: "lastUsed"
                                })
                            )}
                        </div>
                    </div>
                    <div className="row row-form">
                        {this.renderRadioButtonField(
                            "ObstetricalFile_ConceptionAssisted",
                            this.controlRenderer.renderInlineYesNoRadio(
                                this.props.form.Opr1.PregnancySummary.IsAssistedConceptionUsed,
                                value => this.props.mutateModelState(s => s.Opr1.PregnancySummary.IsAssistedConceptionUsed = value),
                                "ConceptionAssisted radio-btn-min-height"
                            ),
                            "col-md-3"
                        )}
                        {this.renderSimpleField(
                            "DetailsLabel",
                            this.controlRenderer.renderTextbox({
                                value: this.props.form.Opr1.PregnancySummary.AssistedConceptionDetails,
                                onChange: value => this.props.mutateModelState(s => s.Opr1.PregnancySummary.AssistedConceptionDetails = value),
                                maxLength: 23,
                                className: "table-textbox"
                            }),
                            "col-md-5"
                        )}
                        {this.props.renderFinalEstimatedBirthDate}
                    </div>
                </div>
                {this.renderDatingMethodOptions()}
            </div>
        );
    }

    private renderPregnancySummaryObstetricalCounters(): JSX.Element {
        return (
            <div className="margin-lg-bottom margin-md-top">
                <ObstetricalCountersComponent
                    readonly={this.props.readonly}
                    renderHeaders
                    obstetricalCounters={this.props.form.ObstetricalCounters}
                    containerId="menstrual-history-fieldsets"
                    onChange={counters => this.props.mutateModelState(s => s.ObstetricalCounters = counters)}
                    onBlur={() => this.props.handleObstetricalCountersUpdate()}
                    additionalFields={[{
                        value: this.props.form.Opr1.PregnancySummary.NeonatalOrChildDeathCount,
                        onChange: value => this.props.mutateModelState(s => s.Opr1.PregnancySummary.NeonatalOrChildDeathCount = value),
                        onBlur: () => this.props.handleFormUpdate(),
                        sideLabel: "",
                        headerLabel: "ObstetricalFile_NeonatalChildDeathLabel"
                    }]}
                    alternateLabels={{
                        top: {
                            [ObstetricalCounter.Premature]: "ObstetricalFile_Preterm",
                            [ObstetricalCounter.Abortion]: "ObstetricalFile_AbortusLabel",
                            [ObstetricalCounter.Live]: "ObstetricalFile_LivingChildrenLabel",
                            [ObstetricalCounter.Stillbirth]: "ObstetricalFile_StillbirthsLabel"
                        }
                    }}
                    locale={this.locale}
                />
            </div>
        );
    }

    private renderDatingMethodOptions(): JSX.Element {
        return (
            <div className="col-md-4">
                <div className="row">
                    {this.renderRadioButtonField(
                        "ObstetricalFile_DatingMethod",
                        this.controlRenderer.renderInlineRadio({
                            value: this.props.form.Opr1.PregnancySummary.DatingMethodValue,
                            items: this.datingMethodOptions([PregnancyDatingMethod.FirstTrimesterUltrasound, PregnancyDatingMethod.SecondTrimesterUltrasound, PregnancyDatingMethod.LastMenstrualPeriod]),
                            onChange: value => this.handleDatingMethod(value),
                            id: "datingmethod"
                        }),
                        "col-md-12"
                    )}
                </div>
                <div className="row">
                    <div className="col-md-6">
                        {this.renderRadioButtonField(
                            "",
                            this.controlRenderer.renderInlineRadio({
                                value: this.props.form.Opr1.PregnancySummary.DatingMethodValue,
                                items: this.datingMethodOptions([PregnancyDatingMethod.IntrauterineInsemination]),
                                onChange: value => this.handleDatingMethod(value),
                                id: "datingmethodIntrauterineInsemination"
                            })
                        )}
                        {this.controlRenderer.renderDateSelector({
                            value: this.props.form.Opr1.PregnancySummary.IntrauterineInseminationProcedureDate,
                            onChange: value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.PregnancySummary.IntrauterineInseminationProcedureDate = value),
                            id: "IntrauterineInseminationProcedureDate",
                            className: "width-100"
                        })}
                    </div>
                    <div className="col-md-6">
                        {this.renderRadioButtonField(
                            "",
                            this.controlRenderer.renderInlineRadio({
                                value: this.props.form.Opr1.PregnancySummary.DatingMethodValue,
                                items: this.datingMethodOptions([PregnancyDatingMethod.EmbryoTransfer]),
                                onChange: value => this.handleDatingMethod(value),
                                id: "datingmethodEmbryo"
                            })
                        )}
                        {this.controlRenderer.renderDateSelector({
                            value: this.props.form.Opr1.PregnancySummary.EmbryoTransferDate,
                            onChange: value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.PregnancySummary.EmbryoTransferDate = value),
                            id: "EmbryoTransferDate",
                            className: "width-100"
                        })}
                    </div>
                </div>
                <div className="row">
                    {this.renderRadioButtonField(
                        "",
                        this.controlRenderer.renderInlineRadio({
                            value: this.props.form.Opr1.PregnancySummary.DatingMethodValue,
                            items: this.datingMethodOptions([PregnancyDatingMethod.Other]),
                            onChange: value => this.handleDatingMethod(value),
                            id: "datingmethodOther"
                        }),
                        "col-md-3"
                    )}
                    {this.renderSimpleField(
                        "",
                        this.controlRenderer.renderTextbox({
                            value: this.props.form.Opr1.PregnancySummary.DatingMethodOtherComment,
                            onChange: value => this.props.mutateModelState(s => s.Opr1.PregnancySummary.DatingMethodOtherComment = value),
                            maxLength: 23,
                            className: ""
                        }),
                        "col-md-8"
                    )}
                </div>
            </div>
        );
    }

    private datingMethodOptions(methods: PregnancyDatingMethod[]): InlineOption<PregnancyDatingMethod>[] {
        const datingMethods = {
            [PregnancyDatingMethod.FirstTrimesterUltrasound]: this.getString("ObstetricalFile_FirstTrimesterUltrasoundAbbreviationLabel"),
            [PregnancyDatingMethod.SecondTrimesterUltrasound]: this.getString("ObstetricalFile_SecondTrimesterUltrasoundAbbreviationLabel"),
            [PregnancyDatingMethod.LastMenstrualPeriod]: this.getString("ObstetricalFile_LastMenstrualPeriodAbbreviationLabel"),
            [PregnancyDatingMethod.IntrauterineInsemination]: this.getString("ObstetricalFile_IntrauterineInseminationAbbreviationLabel"),
            [PregnancyDatingMethod.EmbryoTransfer]: this.getString("ObstetricalFile_EmbryoTransfer"),
            [PregnancyDatingMethod.Other]: this.getString("OtherLabel")
        };
        return methods.map(method => new InlineOption<PregnancyDatingMethod>(method, datingMethods[method]));
    }

    private contraceptiveTypeOptions(): SearchableItemModelT<string>[] {
        return [
            new SearchableItemModelT(this.getString("NoneWordLabel"), "ContraceptiveType_None"),
            new SearchableItemModelT(this.getString("ObstetricalFile_OC"), "ContraceptiveType_OC"),
            new SearchableItemModelT(this.getString("ObstetricalFile_Ring"), "ContraceptiveType_Ring"),
            new SearchableItemModelT(this.getString("ObstetricalFile_Patch"), "ContraceptiveType_Patch"),
            new SearchableItemModelT(this.getString("ObstetricalFile_Condom"), "ContraceptiveType_Condom"),
            new SearchableItemModelT(this.getString("ObstetricalFile_IUD"), "ContraceptiveType_IUD"),
            new SearchableItemModelT(this.getString("ObstetricalFile_Rhythm"), "ContraceptiveType_Rhythm"),
            new SearchableItemModelT(this.getString("ObstetricalFile_Injection"), "ContraceptiveType_Injection"),
            new SearchableItemModelT(this.getString("ObstetricalFile_Implant"), "ContraceptiveType_Implant")
        ];
    }

    private handleDatingMethod(value: PregnancyDatingMethod): void {
        this.props.mutateModelStateWithFormUpdate(s => {
            s.Opr2.Ultrasound.mutate(p => p.UseForDating = false);
            s.Opr1.PregnancySummary.DatingMethodValue = value;

            if (value === PregnancyDatingMethod.LastMenstrualPeriod && !!s.Opr1.PregnancySummary.EstimatedDateOfBirthByLastMenstrualPeriodDate)
                this.updateFinalEstimatedDateOfBirthByLastMenstrualPeriod(s);
        });
    }

    private onChangeLastMenstrualPeriodChange(value: Date): void {
        this.props.mutateModelState(
            s => {
                s.Opr1.PregnancySummary.LastMenstrualPeriodDate = value;
                s.Opr1.PregnancySummary.EstimatedDateOfBirthByLastMenstrualPeriodDate = this.calculateEstimatedDateOfBirth(s.Opr1.PregnancySummary);
                this.updateFinalEstimatedDateOfBirthByLastMenstrualPeriod(s);
            },
            () => this.props.handleFormUpdate()
        );
    }

    private onChangeDaysInCycleChange(value: string): void {
        this.props.mutateModelState(s => {
            s.Opr1.PregnancySummary.MenstrualCycleLength = value ? parseInt(value) : null;
            s.Opr1.PregnancySummary.EstimatedDateOfBirthByLastMenstrualPeriodDate = this.calculateEstimatedDateOfBirth(s.Opr1.PregnancySummary);
            this.updateFinalEstimatedDateOfBirthByLastMenstrualPeriod(s);
        });
    }

    private onChangeIgnoreDaysInCycleChange(): void {
        this.props.mutateModelStateWithFormUpdate(s => {
            s.Opr1.PregnancySummary.IgnoreNumberOfDaysToEstimateDueDate = !s.Opr1.PregnancySummary.IgnoreNumberOfDaysToEstimateDueDate;
            s.Opr1.PregnancySummary.EstimatedDateOfBirthByLastMenstrualPeriodDate = this.calculateEstimatedDateOfBirth(s.Opr1.PregnancySummary);
            this.updateFinalEstimatedDateOfBirthByLastMenstrualPeriod(s);
        });
    }

    private updateFinalEstimatedDateOfBirthByLastMenstrualPeriod(form: OntarioObstetricalFileForm): void {
        if (form.Common.FinalEstimatedDateOfBirth && form.Opr1.PregnancySummary.DatingMethodValue !== PregnancyDatingMethod.LastMenstrualPeriod)
            return;

        form.Common.FinalEstimatedDateOfBirth = form.Opr1.PregnancySummary.EstimatedDateOfBirthByLastMenstrualPeriodDate;
        form.Opr1.PregnancySummary.DatingMethodValue = PregnancyDatingMethod.LastMenstrualPeriod;

        form.updateGestationalAgeAtBirth();
        form.updateSubsequentVisitGestationalAge(this.props.contributionId);
        form.updateAgeAtEstimatedDateOfBirth(
            this.props.patient.IndividualResponse.DateOfBirth,
            form.Opr1.PregnancySummary.EstimatedDateOfBirthByLastMenstrualPeriodDate
        );
    }

    private calculateEstimatedDateOfBirth(pregnancySummary: OntarioObstetricalPregnancySummary): Date {
        if (!this.shouldCalculateEstimatedDateOfBirth(pregnancySummary))
            return null;
        const daysInCycle = pregnancySummary.IgnoreNumberOfDaysToEstimateDueDate ? null : +pregnancySummary.MenstrualCycleLength;
        return ObstetricsCalculator.calculateDateOfBirthBasedOnLastMenstrualPeriod(pregnancySummary.LastMenstrualPeriodDate, daysInCycle);
    }

    private shouldCalculateEstimatedDateOfBirth(pregnancySummary: OntarioObstetricalPregnancySummary): boolean {
        return !!pregnancySummary.LastMenstrualPeriodDate && (pregnancySummary.IgnoreNumberOfDaysToEstimateDueDate || !!pregnancySummary.MenstrualCycleLength);
    }
}

export { OntarioObstetricalFilePregnancySummary };
